import React, { useEffect, useRef, useState } from 'react';
import { Pills, Text } from 'barte-design-system';
import { CaretDown } from '@phosphor-icons/react';
import './GroupSelect.css';

export interface GroupOption {
  value: string;
  label: string;
  isDefault?: boolean;
}

interface GroupSelectProps {
  options: GroupOption[];
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /** Quando informado, mostra pill "Tornar default" nos itens não-default. */
  onSetDefault?: (value: string) => void;
}

export function GroupSelect({ options, value, onChange, placeholder = 'Selecione', onSetDefault }: GroupSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="grpsel-wrapper" ref={rootRef}>
      <button
        type="button"
        className={`grpsel-field${open ? ' is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`grpsel-field-value${selected ? '' : ' is-placeholder'}`}>
          <span className="grpsel-field-bullet" aria-hidden />
          {selected ? selected.label : placeholder}
        </span>
        <span className="grpsel-field-right">
          {selected?.isDefault && (
            <Pills label="default" state="success" variant="fill" size="sm" />
          )}
          <CaretDown size={14} weight="bold" />
        </span>
      </button>

      {open && (
        <div className="grpsel-dropdown">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`grpsel-option${opt.value === value ? ' is-selected' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              <Text variant="body-sm">{opt.label}</Text>
              {opt.isDefault ? (
                <Pills label="default" state="success" variant="fill" size="sm" />
              ) : onSetDefault ? (
                <span
                  className="grpsel-make-default"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onSetDefault(opt.value); setOpen(false); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onSetDefault(opt.value);
                      setOpen(false);
                    }
                  }}
                >
                  <Pills label="Tornar default" state="default" variant="light" size="sm" />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
